<script setup lang="ts">
import {ref, computed, watch, onMounted, useSlots, nextTick} from 'vue';
import DataTable from 'datatables.net-vue3';
import DataTablesCore, {type ConfigColumns} from 'datatables.net-bs5';
import 'datatables.net-buttons-bs5'
import 'datatables.net-buttons/js/buttons.html5';
import jszip from 'jszip';
import pdfmake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { useBootstrapBreakpoint } from '@/composables/useBootstrapBreakpoint';
import type {Api, Config} from "datatables.net";
(pdfmake as any).addVirtualFileSystem(pdfFonts);
DataTablesCore.Buttons.pdfMake(pdfmake);
DataTablesCore.Buttons.jszip(jszip);
// DataTablesCore.type('num', 'className', 'dt-body-right');
// DataTablesCore.type('num-fmt', 'className', 'dt-body-right');
// DataTablesCore.type('date', 'className', 'dt-body-right');
DataTable.use(DataTablesCore);

interface DataViewProps {
  data: any[];                              // Data array
  columns?: ConfigColumns[] | undefined;    // Column definitions
  viewMode?: "table" | "grid" | "custom";   // View control (default: 'grid')
  responsive?: boolean;                     // Auto-switch to grid on small screens (default: true)
  options?: Config | undefined;             // Pass-through DataTables options
  // Stable DOM id for the underlying <table>. REQUIRED when options.stateSave is on: DataTables keys
  // saved sort/page state by the table id, and its auto-generated id is unstable across remounts /
  // navigation. Must be unique per page. Harmless (and unnecessary) when stateSave is off.
  tableId?: string;
  // Highlights the row whose `id` equals this with Bootstrap's `table-active`. Left out, rows' classes are untouched.
  selectedRowId?: string | null;
}

const props = withDefaults(defineProps<DataViewProps>(), {
  viewMode: "grid",
  responsive: true
});

const emit = defineEmits<{
  'row-click': [row: any, index: number, event: Event];
}>();

// Reactive state
const tableRef = ref();
const currentView = ref<'table' | 'grid' | 'custom'>('table');
const currentPageData = ref<any[]>([]);
const gridContainerReady = ref(false);
// Detect if custom grid-card slot is provided (dual-strategy selection)
const slots = useSlots();
const hasCustomGridCard = computed(() => !!slots['grid-card']);
const hasCustomGrid = computed(() => !!slots['custom-grid']);
// Generate unique component ID for Teleport target
const componentId = `dv-${Math.random().toString(36).substr(2, 9)}`;
const gridContainerId = `vue-cards-grid-${componentId}`;
// Responsive breakpoint detection using composable
const isBelowLg = useBootstrapBreakpoint('md');

// Update current page data from DataTable
const updateCurrentPageData = (api: Api<any>) => {
    // Get current page data (filtered and sorted by DataTables)
    currentPageData.value = api.rows({ page: 'current' }).data().toArray();
};

// Computed view based on mode and responsive setting
watch([() => props.viewMode, () => props.responsive, isBelowLg], ([mode, isResponsive, small]) => {
  if (isResponsive && small) {
    // Responsive mode: force grid on small screens
    currentView.value = 'grid';
  } else {
    // Use user's preferred viewMode
    currentView.value = mode;
  }

  // Redraw table to apply new layout
  if (tableRef.value?.dt) {
    tableRef.value.dt.draw('page');
  }
}, { immediate: true });

const markSelectedRow = (api: Api<any>) => {
  // A table that does not pass the prop may set table-active itself, e.g. from rowCallback.
  if (props.selectedRowId === undefined) return;
  api.rows({ page: 'current' }).every(function () {
    const rowNode = this.node() as HTMLTableRowElement | null;
    const selected = props.selectedRowId != null && this.data().id === props.selectedRowId;
    rowNode?.classList.toggle('table-active', selected);
  });
};

// Selecting a row does not redraw the table, so the highlight moves here rather than in drawCallback alone.
watch(() => props.selectedRowId, () => {
  const api = tableRef.value?.dt;
  if (api) markSelectedRow(api);
});

const applyViewClasses = (api: Api<any>) => {
  if (!api) return;

  const table =api.table().node() as HTMLTableElement;
  const tbody = table.querySelector('tbody');

  // Apply classes based on current view mode and strategy
  if (currentView.value === 'grid') {
    table.classList.remove('table-hover'); //remove hover effect in grid mode otherwise it conflicts with card hover

    if (hasCustomGridCard.value) {
      // Vue strategy: hide table, show Vue cards
      table.classList.add('dt-hidden');
      table.classList.remove('dt-table-card-mode');
      tbody?.classList.remove('dt-grid-cards');
    } else {
      // CSS strategy: show table in card mode
      table.classList.remove('dt-hidden');
      table.classList.add('dt-table-card-mode');
      tbody?.classList.add('dt-grid-cards');
    }
  } else if (currentView.value === 'custom') {
    // Custom mode: hide table, show custom grid
    table.classList.remove('table-hover');
    table.classList.add('dt-hidden');
    table.classList.remove('dt-table-card-mode');
    tbody?.classList.remove('dt-grid-cards');
  } else {
    // Table mode: show standard table
    table.classList.add('table-hover'); //TODO: assumes table was hoverable by default
    table.classList.remove('dt-hidden', 'dt-table-card-mode');
    tbody?.classList.remove('dt-grid-cards');
  }
};

// Watch currentView changes and toggle table visibility
watch(currentView, () => {
  
  var api = tableRef.value?.dt;
  applyViewClasses(api);
  if (api) {
    tableRef.value.dt.draw('page');
  }
}, { flush: 'post' });

// DataTables options with layout configuration
const computedOptions = computed(() => {
  
  const baseOptions = {
    ...props.options,
    autoWidth: props?.options?.autoWidth ?? false,
    initComplete: function(settings: any) {
      const api = this.api();
      // Apply view classes once DataTables is ready
      nextTick(() => applyViewClasses(api));
      // Call user's custom initComplete if provided
      if (props?.options?.initComplete) {
        props.options.initComplete.call(this, settings, {});
      }
    },
    drawCallback: function(settings: any) {
      const api = this.api();
      if (currentView.value === 'grid' || currentView.value === 'custom') {
        if (hasCustomGridCard.value || hasCustomGrid.value) {
          // VUE STRATEGY: Update current page data for Vue rendering
          gridContainerReady.value = true;
          updateCurrentPageData(api);
        } else if (currentView.value === 'grid') {

          // CSS STRATEGY: Add data-label attributes for each cell (grid mode only)
          const headerTitles = api.columns().titles();
          api.rows({ page: 'current' })
              .every(function(rowIdx, tableLoop, rowLoop){
                //add card-hover to each tr
                const rowNode = this.node() as HTMLTableRowElement;
                rowNode.classList.add('card-hover');
              })
              .cells(":visible")
              .every( function(this: any, cellRowIndex, cellColIdx, tableLoop, cellLoop) {
                //skip first column as it's the main header
                // if(cellLoop === 0)
                //   return;

                 this.node().setAttribute('data-label', headerTitles[cellColIdx]);
              });
        }

      } else {
        // Table mode: remove data-label attributes and card-hover class
        // Note: .node() returns null for rows not in the current page DOM
        api.rows()
            .every(function (rowIdx, tableLoop, rowLoop) {
              const rowNode = this.node() as HTMLTableRowElement | null;
              rowNode?.classList.remove('card-hover');
            })
            .cells()
            .every(function (this: any, rowIndex, cellColIdx) {
              this.node()?.removeAttribute('data-label');
            });
      }

      markSelectedRow(api);

      // Call user's custom drawCallback if provided
      if (props?.options?.drawCallback) {
        props.options.drawCallback.call(this, settings);
      }
    }
  } as Config;
  
  
  
  // Ensure layout object exists so we can create the bottom container
  if(!baseOptions.layout){
    baseOptions.layout = {};
  }
  baseOptions.layout.bottom = () => {
    // Create container for Vue cards
    const container = document.createElement('div');
    container.id = gridContainerId;
    container.className = 'vue-cards-container';
    return container;
  };
  
  if(!baseOptions.layout.topStart){
    baseOptions.layout.topStart = null; //default to null if not provided (removes the page length selector)
  }

  return baseOptions;
});

// Handle row clicks in grid mode
const handleGridCardClick = (row: any, index: number, event: Event) => {
  emit('row-click', row, index, event);
};

// Handle row clicks in table mode
const handleTableClick = (event: Event) => {
  const target = event.target as HTMLElement;
  const row = target.closest('tbody tr');
  if (row) {
    const dt = tableRef.value?.dt;
    if (dt) {
      const rowData = dt.row(row).data();
      const rowIndex = dt.row(row).index();
      emit('row-click', rowData, rowIndex, event);
    }
  }
};

// Get visible columns from DataTable API
const visibleColumns = computed(() => {
  if (!tableRef.value?.dt) return [];
  const api = tableRef.value.dt;
  return api.columns(":visible").titles();
});

// Guardrail: stateSave keys persisted sort/page by the table id. Without a stable `tableId`,
// DataTables' auto id (DataTables_Table_N) changes on every remount/navigation, so state never
// restores. Warn loudly in dev so this can't silently regress.
onMounted(() => {
  if (import.meta.env.DEV && props.options?.stateSave && !props.tableId) {
    console.warn(
      "[DataView] `options.stateSave` is enabled without a `tableId` prop. DataTables' " +
      "auto-generated table id is unstable across remounts/navigation, so saved sort/page state " +
      "won't reliably restore. Pass a stable, page-unique `tableId`. See DATAVIEW-GUIDELINES.md.",
    );
  }
});

</script>

<template>
  <div class="data-view-wrapper" :class="{ 'view-mode-table': currentView === 'table' }">
    <!-- Single DataTable instance -->
    <DataTable
      ref="tableRef"
      :id="tableId"
      :data="data"
      :class="['table']"
      :options="computedOptions"
      @click="handleTableClick"
    >
      <!-- Pass through all column slots to DataTable -->
      <template v-for="(_, name) in $slots" #[name]="slotProps">
        <slot :name="name" v-bind="slotProps" />
      </template>
    </DataTable>

    <!-- Teleport grid cards to DataTables layout container (Vue strategy) -->
    <Teleport v-if="gridContainerReady && (hasCustomGridCard || hasCustomGrid)" :to="`#${gridContainerId}`">
      <!-- Grid mode: Opinionated card grid -->
      <template v-if="currentView === 'grid'">
        <slot v-if="currentPageData.length === 0" name="empty-state">
          <div class="text-center py-5 text-muted">
            <p>No data available</p>
          </div>
        </slot>

        <div v-else class="row row-cols-1 row-cols-sm-2 row-cols-lg-2 row-cols-xl-3 row-cols-xxl-3 g-3">
        <div
          v-for="(row, index) in currentPageData"
          :key="index"
          class="col"
          @click="handleGridCardClick(row, index, $event)"
        >
          <slot name="grid-card" :row="row" :index="index">
        <!-- Default card template -->

            <div class="card h-100 shadow-sm">
              <div class="card-body">
                <!-- First visible field as prominent header -->
                <div class="mb-3 pb-2 border-bottom">
                  <div class="d-flex align-items-start gap-2">
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-truncate">
                        {{ row[visibleColumns[0]?.data] || 'N/A' }}
                      </h6>
                    </div>
                  </div>
                </div>

                <!-- Remaining visible fields in clean list -->
                <div class="d-flex flex-column gap-2">
                  <div v-for="col in visibleColumns.slice(0)"
                       :key="col.data"
                       class="d-flex">
                    <span class="text-muted small me-2">{{ col.title }}:</span>
                    <span class="small">{{ row[col.data??''] || '—' }}</span>
                  </div>
                </div>
              </div>
            </div>

          </slot>
        </div>
        </div>
      </template>

      <!-- Custom mode: Full control via custom-grid slot -->
      <slot v-else-if="currentView === 'custom'"
            name="custom-grid"
            :rows="currentPageData"
            :handleClick="handleGridCardClick"
            :visibleColumns="visibleColumns">
        <!-- No default for custom mode - user must provide -->
      </slot>
    </Teleport>
  </div>
</template>

<style lang="scss">
// Import Bootstrap mixins for responsive breakpoints
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';
@import 'bootstrap/scss/mixins';
@import 'datatables.net-bs5';

// Note: Not using 'scoped' to allow styling DataTables-generated DOM elements

// ============================================================================
// GRID CARD RENDERING — TWO STRATEGIES (selected at runtime in applyViewClasses)
//
// CSS Strategy (no grid-card slot):
//   Table stays in DOM. Classes dt-table-card-mode + dt-grid-cards reshape it
//   into a CSS Grid. JS sets data-label on each <td>; ::before renders the label.
//
// Vue Strategy (grid-card slot provided):
//   Table hidden via dt-hidden. DataTables still owns pagination/sort/filter.
//   drawCallback syncs currentPageData; <Teleport> renders Vue cards into a
//   <div> injected via layout.bottom — sitting in the natural DT layout flow.
// ============================================================================

// ============================================================================
// CSS-BASED GRID CARD STRATEGY
// All styling via CSS/SCSS - JavaScript only adds data-label attributes
// ============================================================================

// Hide table headers in card mode
table.dt-table-card-mode {
  width: 100% !important;
  display: block !important;
  thead {
    display: none !important;
  }
}

// Transform tbody into CSS Grid container
tbody.dt-grid-cards {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  @include media-breakpoint-up(md) {
    grid-template-columns: repeat(2, 1fr); // md: 2 columns
  }

  @include media-breakpoint-up(lg) {
    grid-template-columns: repeat(3, 1fr); // lg: 3 columns
  }

  @include media-breakpoint-up(xl) {
    grid-template-columns: repeat(4, 1fr); // xl: 4 columns
  }
}

// Transform each <tr> into a Bootstrap 5 card
tbody.dt-grid-cards tr {
  display: flex;
  flex-direction: column;
  //background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  padding: 1rem;
  cursor: pointer;

}

// Clickable rows in table mode
.view-mode-table table.dataTable tbody tr {
  cursor: pointer;
}
// Cell styling with grey label
tbody.dt-grid-cards td {
  display: block;
  padding: 0.25rem 0;
  border: none !important;


  // First cell (primary identifier) - prominent header
  //&:first-child {
  //  font-weight: 600;
  //}


  // Fix for DataTables Vue3 wrapper divs causing new lines
  & > div {
    display: inline-block;
    vertical-align: middle;
  }

  //skip first cell as it's the main header
  //&:not(:first-child) {
  & {
    display: grid;
    grid-template-columns: minmax(5rem, max-content) 1fr;    gap: 0.5rem;

    &::before {
      content: attr(data-label);
      color: var(--bs-secondary-color);
    }
  }
  
}

// ============================================================================
// VUE-BASED GRID CARD STRATEGY (existing)
// ============================================================================

// Hide table when in grid mode (Vue strategy)
table.dt-hidden {
  display: none !important;
}

// Card container styling
.vue-cards-container {
  width: 100%;
  padding: 0;
}

// Hide grid container in table mode
.view-mode-table .vue-cards-container {
  display: none !important;
}



// ============================================================================
// SORT ICON STYLING
// Targets span.dt-column-order inside the flex div.dt-column-header.
// Uses Bootstrap Icons font via $bootstrap-icons-map.
// ============================================================================
@import 'bootstrap-icons/font/bootstrap-icons.scss';

// Mixin: replicates .bi::before rendering properties for use in pseudo-elements.
// Mirrors bootstrap-icons/font/bootstrap-icons.scss `.bi::before` exactly,
// using $bootstrap-icons-font so it stays in sync if the font name ever changes.
@mixin bi-pseudo {
  display: inline-block;
  font-family: $bootstrap-icons-font !important;
  font-style: normal;
  font-weight: normal !important;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  vertical-align: -0.125em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

// DataTables-bs5 right-aligns numeric/date headers AND body cells (and reverses
// the header flex direction). We force left-alignment everywhere so headers and
// data line up, and so the sort icon stays to the right of the title.
.data-view-wrapper table.dataTable {
  th.dt-type-numeric, th.dt-type-date,
  td.dt-type-numeric, td.dt-type-date {
    text-align: left;
  }
}

.data-view-wrapper table.dataTable thead > tr > th {

  // Pull icon left: sit it right next to the title text. DataTables-bs5 ships
  // `flex-direction: row-reverse` for dt-type-numeric / dt-type-date headers,
  // which (combined with our flex-start) flips the title and sort-icon — override
  // it so every column header lays out left-to-right.
  div.dt-column-header {
    justify-content: flex-start;
    flex-direction: row;
    font-size: 0.85em;
  }
  div.dt-column-header span.dt-column-title {
    flex-grow: 0;
  }

  // Ensure sort icons are vertically aligned with text
  span.dt-column-order{
    height: inherit;
  }
  
  // Use Bootstrap Icons font for sort pseudo-elements
  span.dt-column-order::before,
  span.dt-column-order::after {
    @include bi-pseudo;
    margin-left: 0.25em; //space between text and icon
    font-size: 100%; 
    position: relative; // allows for fine-tuning vertical alignment if needed
    display: inline; // ensure icons don't cause line breaks
  }

  // Unsorted: single icon at reduced opacity
  &.dt-orderable-asc span.dt-column-order::before,
  &.dt-orderable-desc span.dt-column-order::before {
    content: '';
  }
  &.dt-orderable-asc span.dt-column-order::after,
  &.dt-orderable-desc span.dt-column-order::after {
    content: map-get($bootstrap-icons-map, 'arrow-down-up');
    opacity: 1;
  }

  // Sorted ascending
  &.dt-ordering-asc span.dt-column-order::before {
    content: '';
  }
  &.dt-ordering-asc span.dt-column-order::after {
    content: map-get($bootstrap-icons-map, 'sort-up-alt');
    opacity: 1;
  }

  // Sorted descending
  &.dt-ordering-desc span.dt-column-order::before {
    content: '';
  }
  &.dt-ordering-desc span.dt-column-order::after {
    content: map-get($bootstrap-icons-map, 'sort-down');
    opacity: 1;
  }

  // Hover on sortable headers
  &.dt-orderable-asc:hover,
  &.dt-orderable-desc:hover {
    color: var(--bs-primary);
    transition: color 0.15s ease-in-out;
  }

  //on hover of th no outline
  &:hover {
    outline: none !important;
  }

}

// ============================================================================
// DATATABLES BUTTONS — TOOLBAR STYLING OVERRIDES
//
// DataTables Buttons concatenates its default `btn btn-secondary` className with whatever the
// consumer passes, so a toolbar button always ends up with the solid Bootstrap class applied
// even when the consumer wants `bb-btn-tertiary-cta` (outline). Force the outline appearance
// via Bootstrap 5.3's button CSS custom properties so consumer styling wins regardless of
// class order. Add a similar block for any future `bb-btn-*` toolbar variants.
// ============================================================================
.dt-buttons .bb-btn-tertiary-cta {
  --bs-btn-bg: transparent;
  --bs-btn-color: var(--bs-secondary);
  --bs-btn-border-color: var(--bs-secondary);
  --bs-btn-hover-bg: var(--bs-secondary);
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-border-color: var(--bs-secondary);
  --bs-btn-active-bg: var(--bs-secondary);
  --bs-btn-active-color: #fff;
  --bs-btn-active-border-color: var(--bs-secondary);
}

// Bootstrap's `.dropdown-menu` relies on Popper to set top/left when opened via Bootstrap's
// JS Dropdown. DataTables Buttons manages its own popover lifecycle and doesn't run Popper, so
// `top` and `left` never get set — the absolutely-positioned menu lands at top:0/left:0 of its
// containing block (the .btn-group), overlaying the button. Anchor it to drop down from the
// button's right edge instead.
.dt-button-collection .dropdown-menu {
  top: 100%;
  right: 0;
  left: auto;
}

</style>
